/**
 * VELOCITY — theme core (task A4)
 *
 * The only global this file creates is `window.Velocity`. Everything else is
 * module-scoped. Per-feature modules (cart.js, search.js, product.js, facets.js)
 * attach to this namespace rather than creating their own.
 *
 * Budget: <= 5KB gzipped. Check with:
 *   gzip -c assets/global.js | wc -c
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * pub/sub — decouples cart mutations from the UI that reacts to them.
   * C4 (cart drawer), C1 (header count bubble) and E3 (buy box) all listen
   * for the same events instead of reaching into each other.
   * ------------------------------------------------------------------- */
  var subscribers = {};

  function subscribe(event, cb) {
    (subscribers[event] = subscribers[event] || []).push(cb);
    // Returns an unsubscribe fn so custom elements can clean up on disconnect.
    return function () {
      subscribers[event] = subscribers[event].filter(function (fn) {
        return fn !== cb;
      });
    };
  }

  function publish(event, data) {
    (subscribers[event] || []).forEach(function (cb) {
      cb(data);
    });
  }

  /* Event name constants — avoids typo-driven silent failures. */
  var EVENTS = {
    cartUpdate: 'cart:update',
    cartError: 'cart:error',
    variantChange: 'variant:change',
    drawerOpen: 'drawer:open',
    drawerClose: 'drawer:close'
  };

  /* ---------------------------------------------------------------------
   * fetch helpers
   * ------------------------------------------------------------------- */

  /**
   * Standard body for Shopify Cart AJAX API calls.
   * `type` is 'javascript' for /cart/*.js endpoints, or 'json' when asking for
   * Section Rendering API output alongside the cart response.
   */
  function fetchConfig(type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + (type || 'json')
      }
    };
  }

  /**
   * POST JSON and parse the response. Shopify returns 4xx with a JSON body
   * carrying `description` on cart errors (e.g. sold out) — we surface that
   * rather than a generic HTTP failure.
   */
  function postJSON(url, body, type) {
    var config = fetchConfig(type);
    config.body = JSON.stringify(body);
    return fetch(url, config).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || res.statusText);
          err.data = data;
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  /* ---------------------------------------------------------------------
   * timing
   * ------------------------------------------------------------------- */

  /** Trailing-edge debounce. Used by C5 predictive search (200ms). */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /** rAF-throttled — for scroll handlers (C1 sticky header) where we want
   *  at most one call per frame rather than a fixed delay. */
  function throttleFrame(fn) {
    var queued = false;
    return function () {
      var args = arguments;
      var ctx = this;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        fn.apply(ctx, args);
      });
    };
  }

  /* ---------------------------------------------------------------------
   * focus management — required by C2 mobile nav, C4 cart drawer,
   * C5 search overlay and E4 size guide modal (WCAG 2.1 AA).
   * ------------------------------------------------------------------- */
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var trapState = null;

  function focusableWithin(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        // offsetParent is null for display:none; also skip zero-size elements
        // so we never focus a collapsed accordion panel.
        return el.offsetParent !== null || el.getClientRects().length > 0;
      }
    );
  }

  /**
   * Trap Tab inside `container` until releaseFocus() is called. Remembers the
   * element that had focus so we can restore it on close.
   */
  function trapFocus(container, elementToFocus, returnTo) {
    releaseFocus();

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = focusableWithin(container);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // `returnTo` wins over the current activeElement. A drawer can be opened
    // programmatically (add-to-cart with no click on its own trigger), and a
    // programmatic .click() does not focus the button in every browser — so
    // inferring the return target from activeElement drops focus to <body>.
    trapState = {
      container: container,
      previous: returnTo || document.activeElement,
      onKeydown: onKeydown
    };

    document.addEventListener('keydown', onKeydown);

    var target = elementToFocus || focusableWithin(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    target.focus();
  }

  function releaseFocus(restore) {
    if (!trapState) return;
    document.removeEventListener('keydown', trapState.onKeydown);
    if (restore !== false && trapState.previous && trapState.previous.focus) {
      trapState.previous.focus();
    }
    trapState = null;
  }

  /* Body scroll lock — paired with the drawers above. Compensates for the
   * scrollbar width so locking does not shift layout (CLS gate in §2). */
  var scrollLocks = 0;

  function lockScroll() {
    if (scrollLocks++ > 0) return;
    var gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = gap > 0 ? gap + 'px' : '';
    document.body.classList.add('scroll-locked');
  }

  function unlockScroll() {
    if (scrollLocks > 0 && --scrollLocks > 0) return;
    scrollLocks = 0;
    document.body.style.paddingRight = '';
    document.body.classList.remove('scroll-locked');
  }

  /* ---------------------------------------------------------------------
   * custom element base
   * ------------------------------------------------------------------- */

  /**
   * Base class for the theme's Web Components. Handles the two things every
   * component here needs: cleaning up pub/sub/event listeners on disconnect,
   * and honouring prefers-reduced-motion (B4 kill-switch).
   *
   * Subclasses implement init() rather than connectedCallback().
   */
  class VelocityElement extends HTMLElement {
    constructor() {
      super();
      this._cleanup = [];
      this._booted = false;
    }

    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      if (typeof this.init === 'function') this.init();
    }

    disconnectedCallback() {
      this._cleanup.forEach(function (fn) {
        fn();
      });
      this._cleanup = [];
      this._booted = false;
      if (typeof this.destroy === 'function') this.destroy();
    }

    /** Add a listener that is removed automatically on disconnect. */
    on(target, event, handler, options) {
      target.addEventListener(event, handler, options);
      this._cleanup.push(function () {
        target.removeEventListener(event, handler, options);
      });
      return this;
    }

    /** Subscribe to a theme event, auto-unsubscribed on disconnect. */
    listen(event, handler) {
      this._cleanup.push(subscribe(event, handler));
      return this;
    }

    reducedMotion() {
      return prefersReducedMotion();
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Registers a custom element, ignoring duplicate definitions so a section
   *  rendered twice on one page (D2 featured collection) cannot throw. */
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ---------------------------------------------------------------------
   * Section Rendering API — shared by C4, C5, F3 and F4.
   * ------------------------------------------------------------------- */

  /**
   * Fetch one rendered section and return it as a DocumentFragment ready to
   * swap in. `url` should already carry any filter/page params.
   */
  function fetchSection(sectionId, url) {
    var target = new URL(url || window.location.href, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return fetch(target.toString())
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        return document.createRange().createContextualFragment(html);
      });
  }

  /* ---------------------------------------------------------------------
   * money — formats using the shop's money_format, injected in theme.liquid.
   * ------------------------------------------------------------------- */
  function formatMoney(cents) {
    var format = (window.Velocity && window.Velocity.moneyFormat) || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      var value = cents / 100;
      if (key === 'amount_no_decimals') return Math.round(value).toLocaleString();
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    });
  }

  /* ---------------------------------------------------------------------
   * export — `routes`, `strings` and `moneyFormat` are set from Liquid in
   * theme.liquid before this file executes.
   * ------------------------------------------------------------------- */
  window.Velocity = Object.assign(window.Velocity || {}, {
    EVENTS: EVENTS,
    subscribe: subscribe,
    publish: publish,
    fetchConfig: fetchConfig,
    postJSON: postJSON,
    debounce: debounce,
    throttleFrame: throttleFrame,
    trapFocus: trapFocus,
    releaseFocus: releaseFocus,
    focusableWithin: focusableWithin,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    prefersReducedMotion: prefersReducedMotion,
    fetchSection: fetchSection,
    formatMoney: formatMoney,
    Element: VelocityElement,
    define: define
  });
})();
